import '../css/app.css';

import { Toaster } from '@/components/ui/sonner';
import { useFlashToast } from '@/hooks/use-flash-toast';
import { queryClient } from '@/lib/query-client.browser';
import { createInertiaApp } from '@inertiajs/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { StrictMode } from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { initializeTheme } from './hooks/use-appearance';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

// Apply the saved theme to <html> before React renders so the first paint
// matches the persisted preference (no flash, no hydration mismatch).
initializeTheme();

function FlashToastBridge() {
    useFlashToast();
    return null;
}

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) =>
        resolvePageComponent(
            `./pages/${name}.tsx`,
            import.meta.glob('./pages/**/*.tsx'),
        ),
    setup({ el, App, props }) {
        if (sentryDsn) {
            Sentry.init({
                dsn: sentryDsn,
                sendDefaultPii: true,
                integrations: [Sentry.browserTracingIntegration()],
                tracesSampleRate: 1.0,
            });
        }

        const root = createRoot(el);

        root.render(
            <StrictMode>
                <QueryClientProvider client={queryClient}>
                    <App {...props}>
                        {({ Component, props: pageProps, key }) => (
                            <>
                                <FlashToastBridge />
                                <Component key={key} {...pageProps} />
                                <Toaster />
                            </>
                        )}
                    </App>
                </QueryClientProvider>
            </StrictMode>,
        );
    },
    progress: {
        color: '#7EDCB1',
    },
});
